import { ConnectionGraph } from "./connections";
import { PlacedObject, valueOperatorKind } from "./objects";

const OP_SYMBOL: Record<string, string> = {
  add: "+", sub: "−", mul: "×", div: "÷", mod: "mod",
  floor: "floor", frac: "frac", round: "round", ceil: "ceil",
  abs: "abs", neg: "neg", sign: "sign",
  sqrt: "√", square: "x²", recip: "1÷x", sin: "sin", cos: "cos",
  random: "rand",
};

export interface ExprLine {
  label: string;
  expr: string;
  mathml: string;
}

/**
 * `prec` / `mlPrec` drive minimal parenthesization (3 = self-contained atom or
 * visually self-grouping construct like mfrac, 2 = multiplicative, 1 = additive,
 * 0 = always wrap as an operand). They differ because the two notations group
 * differently: text div is infix `÷` while MathML div is a self-grouping mfrac.
 * `atomic` marks bare mi/mn — the only bases msup can carry without parens.
 */
interface Rendered {
  text: string;
  ml: string;
  prec: number;
  mlPrec: number;
  atomic: boolean;
}

function escapeMl(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function varRendered(name: string): Rendered {
  return { text: name, ml: `<mi>${escapeMl(name)}</mi>`, prec: 3, mlPrec: 3, atomic: true };
}

function numRendered(value: string): Rendered {
  return { text: value, ml: `<mn>${escapeMl(value)}</mn>`, prec: 3, mlPrec: 3, atomic: true };
}

function textOperand(r: Rendered, min: number): string {
  return r.prec < min ? `(${r.text})` : r.text;
}

function mlOperand(r: Rendered, min: number): string {
  return r.mlPrec < min ? `<mrow><mo>(</mo>${r.ml}<mo>)</mo></mrow>` : r.ml;
}

function binaryRendered(operator: string, sym: string, a: Rendered, b: Rendered): Rendered {
  if (operator === "div") {
    return {
      text: `${textOperand(a, 2)} ÷ ${textOperand(b, 3)}`,
      ml: `<mfrac><mrow>${a.ml}</mrow><mrow>${b.ml}</mrow></mfrac>`,
      prec: 2, mlPrec: 3, atomic: false,
    };
  }
  const mins: Record<string, [number, number, number]> = {
    add: [1, 1, 1], sub: [1, 2, 1], mul: [2, 2, 2], mod: [2, 2, 1],
  };
  const [minL, minR, prec] = mins[operator] ?? [3, 3, 0];
  return {
    text: `${textOperand(a, minL)} ${sym} ${textOperand(b, minR)}`,
    ml: `<mrow>${mlOperand(a, minL)}<mo>${escapeMl(sym)}</mo>${mlOperand(b, minR)}</mrow>`,
    prec, mlPrec: prec, atomic: false,
  };
}

function unaryRendered(operator: string, sym: string, a: Rendered): Rendered {
  if (operator === "sqrt") {
    return { text: `√(${a.text})`, ml: `<msqrt><mrow>${a.ml}</mrow></msqrt>`, prec: 3, mlPrec: 3, atomic: false };
  }
  if (operator === "square") {
    const base = a.atomic ? a.ml : `<mrow><mo>(</mo>${a.ml}<mo>)</mo></mrow>`;
    return { text: `${textOperand(a, 3)}²`, ml: `<msup>${base}<mn>2</mn></msup>`, prec: 3, mlPrec: 3, atomic: false };
  }
  if (operator === "recip") {
    return { text: `1 ÷ ${textOperand(a, 3)}`, ml: `<mfrac><mn>1</mn><mrow>${a.ml}</mrow></mfrac>`, prec: 2, mlPrec: 3, atomic: false };
  }
  if (operator === "neg") {
    return { text: `−${textOperand(a, 3)}`, ml: `<mrow><mo>−</mo>${mlOperand(a, 3)}</mrow>`, prec: 0, mlPrec: 0, atomic: false };
  }
  if (operator === "abs") {
    return { text: `|${a.text}|`, ml: `<mrow><mo>|</mo>${a.ml}<mo>|</mo></mrow>`, prec: 3, mlPrec: 3, atomic: false };
  }
  if (operator === "floor") {
    return { text: `⌊${a.text}⌋`, ml: `<mrow><mo>⌊</mo>${a.ml}<mo>⌋</mo></mrow>`, prec: 3, mlPrec: 3, atomic: false };
  }
  if (operator === "ceil") {
    return { text: `⌈${a.text}⌉`, ml: `<mrow><mo>⌈</mo>${a.ml}<mo>⌉</mo></mrow>`, prec: 3, mlPrec: 3, atomic: false };
  }
  return {
    text: `${sym}(${a.text})`,
    ml: `<mrow><mi>${escapeMl(sym)}</mi><mo>(</mo>${a.ml}<mo>)</mo></mrow>`,
    prec: 3, mlPrec: 3, atomic: false,
  };
}

function isIndeterminate(id: string, memberIds: Set<string>, graph: ConnectionGraph): boolean {
  if (graph.edgesTo(id, "inc").length > 0 || graph.edgesTo(id, "reset").length > 0) {
    return true;
  }
  const op1Edges = graph.edgesTo(id, "operand1In");
  if (op1Edges.length > 0 && !memberIds.has(op1Edges[0].from)) {
    return true;
  }
  const op2Edges = graph.edgesTo(id, "operand2In");
  if (op2Edges.length > 0 && !memberIds.has(op2Edges[0].from)) {
    return true;
  }
  return false;
}

function valueVarName(obj: PlacedObject): string {
  if (obj.type === "count") {
    return obj.name || "count";
  }
  return obj.name || (OP_SYMBOL[obj.operator] ?? obj.operator);
}

/**
 * A count node reads as a call: its name (or `count`) applied to the labels of
 * whatever feeds its `+1` pin — `count(BOX[C4])`. Sources whose label resolves
 * empty are dropped; with no resolvable source the bare name stands alone.
 */
function countRendered(
  id: string,
  obj: PlacedObject,
  graph: ConnectionGraph,
  entityLabel: (id: string) => string,
): Rendered {
  const base = valueVarName(obj);
  const sources = graph.edgesTo(id, "inc")
    .map((e): string => entityLabel(e.from))
    .filter((s): boolean => s !== "");
  if (sources.length === 0) {
    return varRendered(base);
  }
  const args = sources.map((s): string => `<mi>${escapeMl(s)}</mi>`).join("<mo>,</mo>");
  return {
    text: `${base}(${sources.join(", ")})`,
    ml: `<mrow><mi>${escapeMl(base)}</mi><mo>(</mo>${args}<mo>)</mo></mrow>`,
    prec: 3, mlPrec: 3, atomic: false,
  };
}

function renderNode(
  id: string,
  memberIds: Set<string>,
  nodeMap: Map<string, PlacedObject>,
  graph: ConnectionGraph,
  visiting: Set<string>,
  entityLabel: (id: string) => string,
): Rendered {
  const obj = nodeMap.get(id);
  if (!obj) {
    return varRendered("?");
  }
  if (visiting.has(id)) {
    return varRendered(valueVarName(obj));
  }
  if (obj.type === "count") {
    return countRendered(id, obj, graph, entityLabel);
  }
  if (obj.operator === "random") {
    return varRendered(valueVarName(obj));
  }
  const kind = valueOperatorKind(obj.operator);
  if (isIndeterminate(id, memberIds, graph)) {
    return varRendered(valueVarName(obj));
  }
  visiting.add(id);
  const op1Edges = graph.edgesTo(id, "operand1In");
  const op1 = op1Edges.length > 0
    ? renderNode(op1Edges[0].from, memberIds, nodeMap, graph, visiting, entityLabel)
    : numRendered("0");
  let result: Rendered;
  const sym = OP_SYMBOL[obj.operator] ?? obj.operator;
  if (kind === "binary") {
    const op2Edges = graph.edgesTo(id, "operand2In");
    const op2 = op2Edges.length > 0
      ? renderNode(op2Edges[0].from, memberIds, nodeMap, graph, visiting, entityLabel)
      : numRendered(String(obj.operand));
    result = binaryRendered(obj.operator, sym, op1, op2);
  } else {
    result = unaryRendered(obj.operator, sym, op1);
  }
  visiting.delete(id);
  return result;
}

/**
 * Builds one expression per terminal Value in the group, as both a plain string
 * and a self-contained MathML Core fragment (`<math>…</math>`, label included,
 * user names escaped — safe for innerHTML). A terminal is a group-member Value
 * whose `valueOut` feeds no other member's operand input. Indeterminate inputs
 * (inc/reset edges, or sources outside the member set) stop expansion and render
 * as a variable: the node's `name`, falling back to its operator symbol. Count
 * nodes read as calls over their `+1` sources, labelled via `entityLabel`
 * (e.g. `count(BOX[C4], BOX[D4])`). Returns [] when fewer than 2 Value members
 * exist.
 */
function valueMembers(
  groupId: string,
  graph: ConnectionGraph,
  objects: readonly PlacedObject[],
): { memberIds: Set<string>; nodeMap: Map<string, PlacedObject> } {
  const childIds = graph.edgesFrom(groupId)
    .filter((e): boolean => e.fromPort === "transformOut")
    .map((e): string => e.to);
  const memberIds = new Set<string>();
  const nodeMap = new Map<string, PlacedObject>();
  for (const id of childIds) {
    const obj = objects.find((o): boolean => o.id === id);
    if (obj && (obj.type === "calc" || obj.type === "count")) {
      memberIds.add(id);
      nodeMap.set(id, obj);
    }
  }
  return { memberIds, nodeMap };
}

function terminalsOf(memberIds: Set<string>, graph: ConnectionGraph): string[] {
  const nonTerminals = new Set<string>();
  for (const id of memberIds) {
    for (const e of graph.edgesFrom(id)) {
      if (
        e.fromPort === "valueOut" &&
        memberIds.has(e.to) &&
        (e.toPort === "operand1In" || e.toPort === "operand2In")
      ) {
        nonTerminals.add(id);
      }
    }
  }
  return [...memberIds].filter((id): boolean => !nonTerminals.has(id));
}

/**
 * Group-member Value ids in expression reading order: a post-order walk from
 * each terminal (operand 1 subtree, then operand 2, then the node itself),
 * matching the left-to-right order the rendered expressions read in. Members
 * unreachable from any terminal (full cycles) keep their member order at the end.
 */
export function expressionMemberOrder(
  groupId: string,
  graph: ConnectionGraph,
  objects: readonly PlacedObject[],
): string[] {
  const { memberIds } = valueMembers(groupId, graph, objects);
  const visited = new Set<string>();
  const out: string[] = [];
  const walk = (id: string): void => {
    if (!memberIds.has(id) || visited.has(id)) {
      return;
    }
    visited.add(id);
    for (const port of ["operand1In", "operand2In"] as const) {
      const edges = graph.edgesTo(id, port);
      if (edges.length > 0) {
        walk(edges[0].from);
      }
    }
    out.push(id);
  };
  for (const t of terminalsOf(memberIds, graph)) {
    walk(t);
  }
  for (const id of memberIds) {
    if (!visited.has(id)) {
      out.push(id);
    }
  }
  return out;
}

/**
 * A terminal that drives parameters is labelled by the parameter names it
 * overwrites (`bounce = …`) — that is what the expression means to the scene —
 * falling back to the node's own name otherwise.
 */
function terminalLabel(id: string, obj: PlacedObject, graph: ConnectionGraph): string {
  const params: string[] = [];
  for (const e of graph.edgesFrom(id)) {
    if (e.fromPort === "valueOut" && e.toPort === "setParamIn" && e.param !== "" && !params.includes(e.param)) {
      params.push(e.param);
    }
  }
  return params.length > 0 ? params.join(", ") : obj.name;
}

export function buildGroupExpressions(
  groupId: string,
  graph: ConnectionGraph,
  objects: readonly PlacedObject[],
  entityLabel: (id: string) => string,
): ExprLine[] {
  const { memberIds, nodeMap } = valueMembers(groupId, graph, objects);

  if (memberIds.size < 2) {
    return [];
  }

  return terminalsOf(memberIds, graph).map((id): ExprLine => {
    const obj = nodeMap.get(id)!;
    const rendered = renderNode(id, memberIds, nodeMap, graph, new Set<string>(), entityLabel);
    const label = terminalLabel(id, obj, graph);
    const body = label
      ? `<mrow><mi>${escapeMl(label)}</mi><mo>=</mo>${rendered.ml}</mrow>`
      : `<mrow>${rendered.ml}</mrow>`;
    return { label, expr: rendered.text, mathml: `<math>${body}</math>` };
  });
}
